import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Package, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import ProductCard from "@/components/ProductCard";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  useSEO(category ? category.name?.replace(/^\d+\./, '').trim() : 'Kategorie', category ? `${category.name?.replace(/^\d+\./, '').trim()} - kancelarske potreby. ${total} produktu.` : '');

  const page = parseInt(searchParams.get('page') || '1');
  const sort = searchParams.get('sort') || 'name';

  useEffect(() => {
    axios.get(`${API}/categories/${categoryId}`).then(res => setCategory(res.data)).catch(console.error);
  }, [categoryId]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', '24');
    params.set('sort', sort);
    params.set('category_id', categoryId);
    axios.get(`${API}/products?${params.toString()}`)
      .then(res => { setProducts(res.data.products); setTotal(res.data.total); setPages(res.data.pages); })
      .catch(console.error).finally(() => setLoading(false));
  }, [categoryId, page, sort]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) { params.set(key, value); } else { params.delete(key); }
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="category-page">
      <div className="flex flex-col md:flex-row gap-6">
        <aside className="w-full md:w-64 flex-shrink-0">
          <div className="bg-white border border-slate-200 rounded-md p-4 sticky top-32">
            <h3 className="font-semibold text-slate-800 mb-4">{category?.name?.replace(/^\d+\./, '').trim() || 'Kategorie'}</h3>
            {category?.children && category.children.length > 0 && (
              <div className="mb-4">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Podkategorie</label>
                <ul className="space-y-1">
                  {category.children.map(sub => (
                    <li key={sub.id_category}><Link to={`/kategorie/${sub.id_category}`} className="text-sm text-slate-600 hover:text-[#004E98]">{sub.name?.replace(/^\d+\./, '').trim()}</Link></li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Razeni</label>
              <select value={sort} onChange={(e) => updateFilter('sort', e.target.value)} className="w-full h-10 border border-slate-300 rounded-sm px-3 text-sm focus:border-[#004E98] outline-none" data-testid="sort-filter">
                <option value="name">Nazev A-Z</option>
                <option value="price_asc">Cena: nejnizsi</option>
                <option value="price_desc">Cena: nejvyssi</option>
                <option value="newest">Nejnovejsi</option>
              </select>
            </div>
            <Link to="/produkty" className="text-sm text-[#004E98] hover:underline">&#8592; Vsechny produkty</Link>
          </div>
        </aside>

        <main className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-slate-800">{category?.name?.replace(/^\d+\./, '').trim() || 'Kategorie'}</h1>
            <p className="text-sm text-slate-500">{total.toLocaleString()} produktu</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><Package className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p>V teto kategorii nejsou zadne produkty</p></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map(product => (<ProductCard key={product.id_product} product={product} />))}
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => updateFilter('page', p.toString())} className={`w-10 h-10 rounded-sm text-sm font-medium transition-colors ${page === p ? 'bg-[#004E98] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#004E98]'}`}>{p}</button>
                  ))}
                  {pages > 10 && <span className="text-slate-400">...</span>}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
